import ProviderFormConfig from "@components/forms/ProviderForm/form";
import { ProviderFormData } from "@/formTypes/ProviderFormDataType";
import { buildImportFieldsFromForm } from "./core/fieldsFromForm";
import { ImportField } from "./core/types";

/** Como hay que normalizar el valor crudo del archivo antes de guardarlo. */
export type ProviderImportValueKind =
    | "text"
    | "code"
    | "identification"
    | "phone"
    | "phoneCountryCode"
    | "email"
    | "integer"
    | "providerType";

export type ProviderImportField = ImportField & {
    name: keyof ProviderFormData & string;
    kind: ProviderImportValueKind;
};

/**
 * Atributos que no se ofrecen como destino de una columna:
 * - `tipo_identificacion` se infiere siempre del numero de identificacion.
 * - `shipping` esta declarado en `ProviderFormData` (copiado del formType de cliente,
 *   donde ya no existe) pero nunca existio en el modelo `Provider` de Amplify:
 *   escribirlo seria un atributo huerfano en DynamoDB.
 */
const EXCLUDED_FIELDS = new Set<string>(["tipo_identificacion", "shipping"]);

/** Etiquetas propias donde la del formulario no describe bien lo que se va a importar. */
const LABEL_OVERRIDES: Record<string, string> = {
    // El formulario dice "Tipo Cliente" por copy/paste del de clientes.
    tipo: "Tipo de Proveedor (Contado/Credito)",
};

const FIELD_KINDS: Record<string, ProviderImportValueKind> = {
    codigo: "code",
    nombre: "text",
    nombre_comercial: "text",
    identificacion: "identification",
    telefono1: "phone",
    telefono2: "phone",
    telefono1_cd: "phoneCountryCode",
    telefono2_cd: "phoneCountryCode",
    email: "email",
    tipo: "providerType",
    plazo: "integer",
    contacto: "text",
    direccion: "text",
    cuentas_bancarias: "text",
};

/**
 * Campos que existen en el modelo `Provider` pero no en el formulario, porque los inputs
 * de telefono manejan el codigo de pais internamente. Se agregan a mano para poder
 * mapearlos, y tienen un valor por defecto cuando la columna no viene en el archivo.
 */
const EXTRA_FIELDS: ProviderImportField[] = [
    { name: "telefono1_cd", label: "Codigo de pais Telefono 1", kind: "phoneCountryCode" },
    { name: "telefono2_cd", label: "Codigo de pais Telefono 2", kind: "phoneCountryCode" },
];

/**
 * Atributos del proveedor que se pueden mapear, con la etiqueta que ya usa el formulario.
 *
 * Leerlas del formulario en vez de repetirlas evita que el importador y la pantalla de
 * edicion se desincronicen cuando se renombra un campo.
 */
export const buildProviderImportFields = (): ProviderImportField[] =>
    buildImportFieldsFromForm(ProviderFormConfig, {
        excluded: EXCLUDED_FIELDS,
        labelOverrides: LABEL_OVERRIDES,
        kinds: FIELD_KINDS,
        extraFields: EXTRA_FIELDS,
    }) as ProviderImportField[];

export const PROVIDER_IMPORT_FIELDS: ProviderImportField[] = buildProviderImportFields();

export const findProviderImportField = (name?: string | null): ProviderImportField | undefined =>
    PROVIDER_IMPORT_FIELDS.find((field) => field.name === name);

/**
 * Atributos que el usuario esta obligado a mapear para poder continuar.
 *
 * Solo el nombre. A diferencia de clientes, la identificacion no bloquea: hay proveedores
 * locales sin cedula y exigir la columna dejaria el archivo inservible. La falta de
 * identificacion se avisa por fila en la vista previa.
 */
export const REQUIRED_IMPORT_FIELDS: Array<ProviderImportField["name"]> = ["nombre"];
